import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import { cardReviewSettingsFromUser } from "@-/dev.improve.haniverse.lib.cards.card-content";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useMemo } from "react";

import { useLearnCardDestinationSettings } from "#use-learn-card-destination-settings";

/**
 * Everything the Learn deck's flashcard queue is planned from that changes OUTSIDE the deck's own
 * actions: the resolver's settings (New Cards, Introduce New Cards, a review focus, voice study),
 * the study environment and review focus the due reads are narrowed to, the Admin panel's pinned
 * review queue, the learner's review and daily new-card preferences, their script and their tier.
 *
 * The queue is planned once per flashcard and never revised by the deck's own writes; when any of
 * these change it is planned again, at rest (see `queue-invalidated`). Compare by value.
 */
export type FlashcardQueuePlanInputs = ReturnType<typeof useFlashcardQueuePlanInputs>;

export function useFlashcardQueuePlanInputs() {
  const { introduceNewCardsEnabled, reviewRechargeNewCardsEnabled, voiceActive } =
    useLearnCardDestinationSettings();
  const [reviewFocus] = useDeviceSetting("reviewFocus");
  const [studyEnvironment] = useDeviceSetting("studyEnvironment");
  const [pinnedReviewQueue] = useDeviceSetting("pinnedReviewQueue");
  const user = useActorUser();
  const isPremium = useIsPremium();
  const reviewSettings = JSON.stringify(cardReviewSettingsFromUser(user));
  const { dailyNewGraphemeLimit, hanziScript } = user;
  return useMemo(
    () => ({
      dailyNewGraphemeLimit,
      hanziScript,
      introduceNewCardsEnabled,
      isPremium,
      pinnedReviewQueue: JSON.stringify(pinnedReviewQueue),
      reviewFocus,
      reviewRechargeNewCardsEnabled,
      reviewSettings,
      studyEnvironment,
      voiceActive,
    }),
    [
      dailyNewGraphemeLimit,
      hanziScript,
      introduceNewCardsEnabled,
      isPremium,
      pinnedReviewQueue,
      reviewFocus,
      reviewRechargeNewCardsEnabled,
      reviewSettings,
      studyEnvironment,
      voiceActive,
    ],
  );
}
